// @ts-nocheck
import { canonicalStoreSelectionBrand } from '@theme/utilities';

const STORAGE_KEY = 'amb_store_selection';
const EVENT_CHANGED = 'store-selection:changed';
const EVENT_CLEARED = 'store-selection:cleared';
const BRANDS = new Set(['bakers', 'cobs']);
const COUNTRY_CODES = new Set(['AU', 'CA', 'US']);
const PICKUP_DAYS = new Set(['today', 'tomorrow']);

let storageBridgeBound = false;

const canUseLocalStorage = () => {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch (_) {
    return false;
  }
};

const safeParse = (value) => {
  if (typeof value !== 'string' || value.trim() === '') return null;

  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
};

const sanitizeString = (value) => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const normalizeCountryCode = (value) => sanitizeString(value).toUpperCase();
const normalizePickupDay = (value) => sanitizeString(value).toLowerCase();
const nowIso = () => new Date().toISOString();

const normalizeAddress = (value) => {
  if (!value || typeof value !== 'object') return null;
  const address1 = sanitizeString(value.address1);
  const address2 = sanitizeString(value.address2);
  const city = sanitizeString(value.city);
  const province = sanitizeString(value.province);
  const provinceCode = sanitizeString(value.provinceCode);
  const zip = sanitizeString(value.zip);
  const country = sanitizeString(value.country);
  if (!address1 && !address2 && !city && !province && !provinceCode && !zip && !country) return null;
  return {
    address1,
    address2,
    city,
    province,
    provinceCode,
    zip,
    country
  };
};

const normalizeCoordinates = (value) => {
  if (!value || typeof value !== 'object') return null;
  const latitude = Number(value.latitude);
  const longitude = Number(value.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
};

/** `amb_store_selection` is flat only: no `details`, `status`, or nested `metafields` (see `.cursor/rules/amb-localstorage-mf10-parity.mdc`). Legacy rows may still include `details`; we hoist distance/address/coordinates then drop it. */
const normalizeSelection = (input) => {
  if (!input || typeof input !== 'object') return null;

  const storeId = sanitizeString(input.storeId);
  const slug = sanitizeString(input.slug);
  const name = sanitizeString(input.name);
  const locationName = sanitizeString(input.locationName);
  const brand = canonicalStoreSelectionBrand(input.brand);
  const countryCode = normalizeCountryCode(input.countryCode);
  const pickupDay = normalizePickupDay(input.pickupDay);
  const pickupSlot = sanitizeString(input.pickupSlot);
  const updatedAt = sanitizeString(input.updatedAt) || nowIso();

  const legacy = input.details && typeof input.details === 'object' ? input.details : null;
  const distanceKmSource = input.distanceKm !== undefined && input.distanceKm !== null
    ? input.distanceKm
    : legacy?.distanceKm;
  const dk = Number(distanceKmSource);
  const distanceKm = Number.isFinite(dk) ? dk : null;

  const address =
    normalizeAddress(input.address) ?? (legacy?.address ? normalizeAddress(legacy.address) : null);

  const coordinates =
    normalizeCoordinates(input.coordinates) ??
    (legacy?.coordinates ? normalizeCoordinates(legacy.coordinates) : null);

  const internalBakeryIdRaw =
    input.internalBakeryId !== undefined && input.internalBakeryId !== null
      ? String(input.internalBakeryId).trim()
      : '';
  const internalBakeryId = internalBakeryIdRaw !== '' ? internalBakeryIdRaw : null;

  // IANA timezone of the selected bakery (from the location's `amb.timezone`
  // metafield). Consumed by availability.js `isInventoryWindowActive` for the
  // MF36 inventory-enforcement window (MO08 local bakery time bounds).
  const timezoneRaw =
    input.timezone !== undefined && input.timezone !== null ? String(input.timezone).trim() : '';
  const timezone = timezoneRaw !== '' ? timezoneRaw : null;

  // Deliverect arrival URL of the selected bakery (from the location's
  // `custom.mf35_location_deliverect_url` metafield). Synced to the
  // `amb_deliverect_url` cart attribute by utilities.js `syncCartAttributes`
  // so the order-confirmation email can render "Notify staff you've arrived".
  const deliverectUrlRaw =
    input.deliverectUrl !== undefined && input.deliverectUrl !== null
      ? String(input.deliverectUrl).trim()
      : '';
  const deliverectUrl = deliverectUrlRaw !== '' ? deliverectUrlRaw : null;

  const base = {
    storeId,
    slug,
    name,
    brand,
    countryCode,
    updatedAt,
    distanceKm,
    address,
    coordinates
  };

  if (internalBakeryId) base.internalBakeryId = internalBakeryId;
  if (timezone) base.timezone = timezone;
  if (deliverectUrl) base.deliverectUrl = deliverectUrl;
  if (locationName) base.locationName = locationName;

  if (pickupDay) base.pickupDay = pickupDay;
  if (pickupSlot) base.pickupSlot = pickupSlot;

  return base;
};

export const isSelectionValid = (selection) => {
  if (!selection || typeof selection !== 'object') return false;

  if (sanitizeString(selection.storeId) === '') return false;
  if (sanitizeString(selection.slug) === '') return false;
  if (sanitizeString(selection.name) === '') return false;
  if (!BRANDS.has(canonicalStoreSelectionBrand(selection.brand))) return false;
  if (!COUNTRY_CODES.has(normalizeCountryCode(selection.countryCode))) return false;

  const pickupDay = sanitizeString(selection.pickupDay);
  if (pickupDay !== '' && !PICKUP_DAYS.has(normalizePickupDay(pickupDay))) return false;

  const updatedAt = sanitizeString(selection.updatedAt);
  if (updatedAt === '') return false;
  if (Number.isNaN(Date.parse(updatedAt))) return false;

  if (selection.distanceKm != null && !Number.isFinite(Number(selection.distanceKm))) return false;
  if (
    selection.address !== undefined &&
    selection.address !== null &&
    typeof selection.address !== 'object'
  ) {
    return false;
  }
  if (
    selection.coordinates !== undefined &&
    selection.coordinates !== null &&
    typeof selection.coordinates !== 'object'
  ) {
    return false;
  }
  if (
    selection.internalBakeryId !== undefined &&
    selection.internalBakeryId !== null &&
    typeof selection.internalBakeryId !== 'string'
  ) {
    return false;
  }
  if (
    selection.timezone !== undefined &&
    selection.timezone !== null &&
    typeof selection.timezone !== 'string'
  ) {
    return false;
  }
  if (
    selection.deliverectUrl !== undefined &&
    selection.deliverectUrl !== null &&
    typeof selection.deliverectUrl !== 'string'
  ) {
    return false;
  }
  if ('details' in selection || 'status' in selection || 'metafields' in selection) return false;

  return true;
};

/** No bakery saved — pickup is not applicable, so nothing should be prompted. */
export const PICKUP_PROMPT_NONE = 'none';
/** Bakery + day + a slot that is still in the future. */
export const PICKUP_PROMPT_OK = 'ok';
/** Bakery saved with no pickup day and/or no slot (e.g. an MF10-hydrated row). */
export const PICKUP_PROMPT_MISSING = 'missing';
/** Bakery + pickup saved, but the relative day or the slot has since elapsed. */
export const PICKUP_PROMPT_EXPIRED = 'expired';

const PICKUP_SLOT_RANGE_PATTERN = /^(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})$/;

/**
 * Re-express an instant as a wall-clock `Date` in the bakery's own timezone, so
 * calendar-day and time-of-day comparisons are made in the timezone the shopper
 * booked against rather than the browser's. Mirrors `#nowInTimezone` in
 * assets/store-selector-drawer.js. Falls back to browser local time when the
 * selection has no `timezone` (pre-MF35 rows) or the zone is unusable.
 *
 * @param {Date} instant
 * @param {string | null | undefined} timezone IANA zone from `amb.timezone`
 * @returns {Date}
 */
const wallClockInTimezone = (instant, timezone) => {
  const tz = sanitizeString(timezone);
  if (tz === '') return new Date(instant.getTime());

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(instant);

    const read = (type) => Number(parts.find((part) => part.type === type)?.value);
    const year = read('year');
    const month = read('month');
    const day = read('day');
    // `hour12: false` reports midnight as 24 in some engines.
    const hour = read('hour') % 24;
    const minute = read('minute');
    if (![year, month, day, hour, minute].every(Number.isFinite)) {
      return new Date(instant.getTime());
    }
    return new Date(year, month - 1, day, hour, minute, 0, 0);
  } catch (_) {
    return new Date(instant.getTime());
  }
};

const isSameCalendarDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * @param {string} slot e.g. `"14:00-15:00"`
 * @returns {{ start: number, end: number } | null} minutes past midnight
 */
const parsePickupSlotMinutes = (slot) => {
  const match = PICKUP_SLOT_RANGE_PATTERN.exec(sanitizeString(slot));
  if (!match) return null;
  const [, startHour, startMinute, endHour, endMinute] = match;
  return {
    start: Number(startHour) * 60 + Number(startMinute),
    end: Number(endHour) * 60 + Number(endMinute)
  };
};

/**
 * Whether the shopper still needs to choose a collection time, and why.
 *
 * `pickupDay` and `pickupSlot` are optional in the persisted row (see
 * `normalizeSelection`), so a bakery-without-pickup selection is structurally
 * valid and every other consumer treats it as complete. Two things produce one:
 * MF10 hydration, which only re-attaches pickup from an existing localStorage
 * row for the same bakery (new device, cleared storage, or a different saved
 * bakery all drop it), and rows written before the pickup panel shipped.
 *
 * Staleness is the same problem one day later: `pickupDay` is relative, so a
 * `today` row saved yesterday now claims a day the shopper never chose, and a
 * `today` slot whose window has closed can no longer be collected. Both are
 * reported as `expired` rather than silently carried into cart attributes.
 *
 * @param {object | null | undefined} selection
 * @param {Date} [now] injectable for tests
 * @returns {typeof PICKUP_PROMPT_NONE | typeof PICKUP_PROMPT_OK | typeof PICKUP_PROMPT_MISSING | typeof PICKUP_PROMPT_EXPIRED}
 */
export const getPickupPromptState = (selection, now = undefined) => {
  if (!selection || typeof selection !== 'object') return PICKUP_PROMPT_NONE;
  if (sanitizeString(selection.storeId) === '') return PICKUP_PROMPT_NONE;
  if (sanitizeString(selection.name) === '') return PICKUP_PROMPT_NONE;

  const pickupDay = normalizePickupDay(selection.pickupDay);
  const pickupSlot = sanitizeString(selection.pickupSlot);
  if (!PICKUP_DAYS.has(pickupDay) || pickupSlot === '') return PICKUP_PROMPT_MISSING;

  const nowInstant = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  const savedAt = Date.parse(sanitizeString(selection.updatedAt));
  // Unparseable timestamps only come from hand-mangled storage, and `getSelection`
  // rejects them anyway. Prompting on one would be a guess, so leave it alone.
  if (Number.isNaN(savedAt)) return PICKUP_PROMPT_OK;

  const timezone = selection.timezone;
  const nowLocal = wallClockInTimezone(nowInstant, timezone);
  const savedLocal = wallClockInTimezone(new Date(savedAt), timezone);

  // The day rolled over, so `today` / `tomorrow` no longer mean what was chosen.
  if (!isSameCalendarDay(nowLocal, savedLocal)) return PICKUP_PROMPT_EXPIRED;
  if (pickupDay !== 'today') return PICKUP_PROMPT_OK;

  const minutes = parsePickupSlotMinutes(pickupSlot);
  if (!minutes) return PICKUP_PROMPT_OK;
  // A window ending at or before it starts closes after midnight (e.g. 23:00-00:00),
  // so it cannot have elapsed while we are still on the same calendar day.
  if (minutes.end <= minutes.start) return PICKUP_PROMPT_OK;

  const nowMinutes = nowLocal.getHours() * 60 + nowLocal.getMinutes();
  // Measured against the end of the window: a shopper who booked 07:00-08:00 can
  // still collect at 07:30.
  return nowMinutes >= minutes.end ? PICKUP_PROMPT_EXPIRED : PICKUP_PROMPT_OK;
};

/**
 * True when a bakery is saved but the shopper still owes us a collection time.
 *
 * @param {object | null | undefined} selection
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isPickupPromptRequired = (selection, now = undefined) => {
  const state = getPickupPromptState(selection, now);
  return state === PICKUP_PROMPT_MISSING || state === PICKUP_PROMPT_EXPIRED;
};

const emitEvent = (name, detail) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
};

const handleStorageChange = (event) => {
  if (!event || event.key !== STORAGE_KEY) return;

  const parsed = safeParse(event.newValue);
  const normalized = normalizeSelection(parsed);

  if (!normalized || !isSelectionValid(normalized)) {
    emitEvent(EVENT_CLEARED, { source: 'storage' });
    return;
  }

  emitEvent(EVENT_CHANGED, {
    source: 'storage',
    selection: normalized
  });
};

const bindStorageBridge = () => {
  if (storageBridgeBound) return;
  if (typeof window === 'undefined') return;

  window.addEventListener('storage', handleStorageChange);
  storageBridgeBound = true;
};

const unbindStorageBridge = () => {
  if (!storageBridgeBound) return;
  if (typeof window === 'undefined') return;

  window.removeEventListener('storage', handleStorageChange);
  storageBridgeBound = false;
};

export const getSelection = () => {
  if (!canUseLocalStorage()) return null;

  const raw = window.localStorage.getItem(STORAGE_KEY);
  const parsed = safeParse(raw);
  const normalized = normalizeSelection(parsed);

  if (!normalized || !isSelectionValid(normalized)) return null;
  return normalized;
};

export const setSelection = (payload) => {
  if (!canUseLocalStorage()) return null;

  const normalized = normalizeSelection(payload);
  if (!normalized || !isSelectionValid(normalized)) return null;

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  emitEvent(EVENT_CHANGED, {
    source: 'local',
    selection: normalized
  });

  return normalized;
};

export const clearSelection = () => {
  if (!canUseLocalStorage()) return false;

  window.localStorage.removeItem(STORAGE_KEY);
  emitEvent(EVENT_CLEARED, { source: 'local' });

  return true;
};

export const subscribe = (handler, options = {}) => {
  if (typeof window === 'undefined' || typeof handler !== 'function') {
    return () => {};
  }

  const includeStorage = options.includeStorage !== false;
  const onChanged = (event) => handler({ type: EVENT_CHANGED, detail: event.detail });
  const onCleared = (event) => handler({ type: EVENT_CLEARED, detail: event.detail });

  window.addEventListener(EVENT_CHANGED, onChanged);
  window.addEventListener(EVENT_CLEARED, onCleared);

  if (includeStorage) bindStorageBridge();

  return () => {
    window.removeEventListener(EVENT_CHANGED, onChanged);
    window.removeEventListener(EVENT_CLEARED, onCleared);
  };
};

const api = {
  STORAGE_KEY,
  EVENT_CHANGED,
  EVENT_CLEARED,
  PICKUP_PROMPT_NONE,
  PICKUP_PROMPT_OK,
  PICKUP_PROMPT_MISSING,
  PICKUP_PROMPT_EXPIRED,
  getSelection,
  setSelection,
  clearSelection,
  isSelectionValid,
  getPickupPromptState,
  isPickupPromptRequired,
  subscribe,
  bindStorageBridge,
  unbindStorageBridge
};

if (typeof window !== 'undefined') {
  bindStorageBridge();
  window.StoreSelectionState = api;
}

export default api;
